import { Plugin } from "prosemirror-state";
import { EditorView } from "prosemirror-view";

import { POPUP_MANAGER_PLUGIN } from "@/wc/text-editor/lib/core/popups";
import { type LinkedEntity } from ".";
import { type LinkedEntitiesManager } from "./manager";

/**
 * Update domain entity nodes based on data from the repository and
 * show popups with the descripton of the domain entities.
 */
export const createInfoPlugin = <E extends LinkedEntity>(
    nodeType: string,
    popup: {
        key: string;
        hover: boolean;
        create: (
            entity: Promise<E | void>,
            entityId: string
        ) => (
            view: EditorView
        ) => Promise<Element | undefined> | Element | undefined;
    },
    getManager: () => LinkedEntitiesManager<E>
) =>
    new Plugin({
        view(view) {
            const popupManagerState = POPUP_MANAGER_PLUGIN.getState(view.state);
            if (!popupManagerState) return {};

            const entitiesManager = getManager();

            popupManagerState.handlers[popup.key] = (node, cursor, _mark) => {
                return {
                    startPos: cursor.startPos,
                    endPos: cursor.endPos,
                    onlyInEditMode: false,
                    create: (view) => {
                        const entityPromise = entitiesManager.ready.then(() => {
                            if (node.attrs.state === "external") {
                                return entitiesManager.extract(
                                    node.attrs.href,
                                    node.attrs.name
                                );
                            }
                            return entitiesManager.findById(
                                node.attrs.entityId
                            );
                        });
                        return popup.create(
                            entityPromise,
                            node.attrs.entityId
                        )(view);
                    },
                    node,
                    closeOnMouseLeave: true,
                };
            };

            const updateEntities = () => {
                const startState = view.state;
                let updateEntitiesTr = startState.tr;
                startState.doc.descendants((node, pos) => {
                    if (node.type === view.state.schema.nodes[nodeType]) {
                        const entity = entitiesManager.findById(
                            node.attrs.entityId
                        );
                        if (entity) {
                            updateEntitiesTr = updateEntitiesTr.setNodeMarkup(
                                updateEntitiesTr.mapping.map(pos),
                                undefined,
                                { entityId: entity.id, ...entity }
                            );
                        } else if (
                            entitiesManager.entities.length &&
                            node.attrs.state === "pending"
                        ) {
                            updateEntitiesTr =
                                updateEntitiesTr.setNodeAttribute(
                                    updateEntitiesTr.mapping.map(pos),
                                    "state",
                                    "gone"
                                );
                        }
                    }
                });
                view.dispatch(updateEntitiesTr.setMeta("addToHistory", false));
            };

            entitiesManager.events.addEventListener(
                "entities:changed",
                updateEntities
            );

            updateEntities();

            return {
                destroy() {
                    entitiesManager.events.removeEventListener(
                        "entities:changed",
                        updateEntities
                    );
                },
            };
        },
    });
